import { PureComponent } from 'react';
import { connect } from 'react-redux';

import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { change, getFormInitialValues, getFormValues } from 'redux-form';

import i18n, { withTransl } from 'shared/lib/i18n';

import BrokerFormComponent from './BrokerFormComponent';

import { getDoors } from '../AccessPoint/actionCreators';
import { getBuildingsForFilter } from '../Building/actionCreators';
import { getAllPmcReducedData, getPmcDoors } from '../PMC/actionCreators';
import {
  confirmToBePermanent,
  getVisitorInfoById,
  onPersonSubmit,
  onSubmitComplete,
} from './actionCreators';

import { filterObjectByKeyValues, formatDate } from '../../utils';
import {
  findPersonEmail,
  findPersonPhoneNumber,
  getFullName,
} from '../Person/utils';
import { buildingsForFilterTypes } from '../Shared/utils/types';
import { formatDoorName, getBrokerDoors } from './utils';

import { history } from '../../store';

const form = 'broker';

const validate = (values) => {
  const errors = {};
  if (!values?.pmc && !values?.buildings?.length) {
    const errMessage = i18n.t('errors.pmcOrBuildingRequired');
    errors.pmc = errMessage;
    errors.buildings = errMessage;
  }
  return errors;
};

const pMCsIdsFromBuildings = (filteredBuildings, formValuesBuildings) => {
  const filterBuildingsValues = Object.values(filteredBuildings);
  const buildingIdsFormValue = formValuesBuildings || [];
  return Array.from(
    filterBuildingsValues
      .filter(({ id }) => buildingIdsFormValue.includes(id))
      .reduce(
        (parentsIds, building) => parentsIds.add(building?.parent?.id),
        new Set(),
      ),
  );
};

const getDoorsFromBuilding = (
  filteredBuildings,
  formValuesBuildings,
  isPMCSelected,
) => {
  const filterBuildingsValues = Object.values(filteredBuildings);
  const buildingIdsFormValue = formValuesBuildings || [];
  const selectedPMCIds = pMCsIdsFromBuildings(
    filteredBuildings,
    formValuesBuildings,
  );
  const buildingsFromUnselectedPMC = filterBuildingsValues
    .filter(({ parent }) => !selectedPMCIds.includes(parent?.id))
    ?.map(({ id }) => id);
  return isPMCSelected
    ? [...buildingsFromUnselectedPMC, ...buildingIdsFormValue]
    : buildingIdsFormValue;
};

const getDefaultDoors = (doors) =>
  doors
    ?.filter(({ defaultInBuilding }) => defaultInBuilding)
    ?.map(({ id }) => id) || [];

class BrokerFormContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      initialValues: {},
      preselectedProperty: null,
      showWarning: false,
    };
    this.onLoad = this.onLoad.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.completeBroker = this.completeBroker.bind(this);
    this.showWarning = this.showWarning.bind(this);
    this.closeWarning = this.closeWarning.bind(this);
    this.getInitialValues = this.getInitialValues.bind(this);
    this.getPreselectedInitialValues =
      this.getPreselectedInitialValues.bind(this);
    this.updateDefaultDoors = this.updateDefaultDoors.bind(this);
  }

  componentDidMount() {
    const { getPmcs, getBuildings, pmcId, buildingId } = this.props;

    !pmcId && !buildingId && getPmcs();
    !buildingId && getBuildings();
  }

  componentDidUpdate(prevProps) {
    const {
      completeData,
      formValues,
      getPmcDoors,
      getBuildingDoors,
      isUpdate,
      updateFormValue,
      filteredBuildings,
    } = this.props;

    const selectedPmc = formValues?.pmc;
    const prevPmc = prevProps?.formValues?.pmc;

    const selectedBuildings = formValues?.buildings;

    const prevBuildings = prevProps?.formValues?.buildings;

    const selectedDoorsIds = formValues?.doors;

    // shown if person is already exist
    if (!isEmpty(completeData) && prevProps.completeData !== completeData) {
      this.showWarning();
    }

    const filteredBuildingsArray = Object.values(filteredBuildings)?.map(
      ({ id }) => id,
    );

    if (!isUpdate && selectedPmc && !isEqual(selectedPmc, prevPmc)) {
      // if there is a new pmc selected -> fetch doors for it

      const newPMCId =
        selectedPmc?.length &&
        selectedPmc?.find((id) => !prevPmc?.includes(id));
      newPMCId && getPmcDoors(newPMCId, this.updateDefaultDoors);

      if (selectedBuildings?.length) {
        const relevantBuildingsIds = selectedBuildings?.filter((id) =>
          filteredBuildingsArray?.includes(id),
        );
        if (!isEqual(selectedBuildings, relevantBuildingsIds)) {
          updateFormValue('buildings', relevantBuildingsIds);
        }
      } else if (selectedDoorsIds?.length) {
        this.updateRelevantDoors();
      }
    }

    if (!isUpdate && !isEqual(selectedBuildings, prevBuildings)) {
      // if there is a new building selected -> fetch doors for it
      const newSelectedBuildingId = selectedBuildings?.find(
        (id) => !prevBuildings?.includes(id),
      );

      newSelectedBuildingId &&
        !selectedPmc &&
        getBuildingDoors(newSelectedBuildingId, this.updateDefaultDoors);

      // make sure selected doors ids match selected buildings
      // if there are doors for unchecked buildings - remove them
      this.updateRelevantDoors();
    }
  }

  async onLoad() {
    const {
      getBrokerById,
      brokerId,
      isBuildingLevel,
      isPmcLevel,
      propertyId,
      propertyName,
      getPmcDoors,
      getBuildingDoors,
    } = this.props;
    if (isBuildingLevel || isPmcLevel) {
      return this.getPreselectedInitialValues({
        isBuildingLevel,
        propertyId,
        propertyName,
      });
    }
    if (brokerId) {
      const broker = await getBrokerById(brokerId);
      const { pmc, building } = broker ?? {};
      building && getBuildingDoors(building);
      !building && pmc && getPmcDoors(pmc);
      return broker?.id && this.getInitialValues(broker);
    }
    return null;
  }

  async onSubmit(data) {
    const { onSubmit } = this.props;

    const { buildings, pmc, ...rest } = data;
    const properties = buildings?.length > 0 ? buildings : pmc;
    const res = await onSubmit({ ...rest, properties });

    return (res?.created || res?.updated) && history.goBack();
  }

  getInitialValues(broker) {
    const { t } = this.props;
    const {
      person,
      pmc,
      building,
      authorizer,
      creator,
      validUntil,
      comments,
      invite,
    } = broker;
    const { firstName, lastName, photo } = person || {};
    const pmcItem = typeof pmc === 'string' ? Number(pmc) : pmc;
    const initialValues = {
      pmc: pmcItem ? [pmcItem] : [],
      buildings: building ? [building] : [],
      firstName,
      lastName,
      email: findPersonEmail(person),
      phoneNumber: findPersonPhoneNumber(person),
      contactPerson: authorizer
        ? getFullName(authorizer)
        : creator
        ? getFullName(creator)
        : t('roles.carsonAdmin'),
      avatar: photo?.url,
      validUntil: formatDate(validUntil, true, null),
      comments,
      validUntilInfo: validUntil
        ? formatDate(validUntil, false)
        : t('roles.permanentBroker'),
      doors: invite?.doors || [],
    };
    this.setState({
      initialValues,
    });
  }

  getPreselectedInitialValues(data) {
    const { isBuildingLevel, propertyId, propertyName } = data;
    const initialPropertyKey = isBuildingLevel ? 'buildings' : 'pmc';
    const initialPropertyValue = isBuildingLevel ? [propertyId] : [propertyId];
    const initialValues = {
      [initialPropertyKey]: initialPropertyValue,
    };
    const preselectedProperty = [{ label: propertyName, value: propertyId }];
    this.setState({
      initialValues,
      preselectedProperty,
    });
  }

  updateRelevantDoors() {
    const { doors, updateFormValue, formValues, filteredBuildings } =
      this.props;
    const filteredBuildingsArray = Object.values(filteredBuildings)?.map(
      ({ id }) => id,
    );
    const selectedDoorsIds = formValues?.doors;
    const selectedDoors = doors?.filter(({ id }) =>
      selectedDoorsIds?.includes(id),
    );
    const relevantDoorsIds = selectedDoors.length
      ? selectedDoors
          ?.filter(({ building }) => filteredBuildingsArray?.includes(building))
          ?.map(({ id }) => id)
      : [];
    const relevantDoorsIdsWithDefault = Array.from(
      new Set([...relevantDoorsIds, ...getDefaultDoors(doors)]),
    );
    if (!isEqual(selectedDoorsIds, relevantDoorsIdsWithDefault)) {
      updateFormValue('doors', relevantDoorsIdsWithDefault);
    }
  }

  updateDefaultDoors(doors) {
    const { formValues, updateFormValue } = this.props;
    const selectedDoorsIds = formValues?.doors || [];
    const defaultBuildingDoorsIds = getDefaultDoors(doors);
    updateFormValue('doors', [
      ...new Set([...selectedDoorsIds, ...defaultBuildingDoorsIds]),
    ]);
  }

  async completeBroker(e) {
    const {
      completeData,
      onSubmitComplete,
      formValues: { buildings, pmc, ...rest },
    } = this.props;
    const chosenBroker = !isEmpty(completeData)
      ? find(completeData, (data) => data.id === Number(e.id))
      : [];
    const brokerData = {
      ...rest,
      ...chosenBroker,
      person: chosenBroker.id,
      properties: buildings?.length > 0 ? buildings : pmc,
    };
    await onSubmitComplete(brokerData);
    this.closeWarning();
    history.goBack();
  }

  showWarning() {
    this.setState({
      showWarning: true,
    });
  }

  closeWarning() {
    this.setState({
      showWarning: false,
    });
  }

  render() {
    const { initialValues, showWarning, preselectedProperty } = this.state;
    const {
      isBuildingLevel,
      isPmcLevel,
      pmcs,
      filteredBuildings: buildings,
      doors,
      isEditable,
      isUpdate,
      shouldBePermanent,
      hasSelectedProperty,
      formValues,
      formInitialValues,
      match,
    } = this.props;

    const props = {
      initialValues: preselectedProperty || isUpdate ? initialValues : {},
      onSubmit: this.onSubmit,
      onLoad: this.onLoad,
      formValues,
      formInitialValues,
      isUpdate,
      isEditable,
      pmcs,
      buildings,
      doors,
      isWarningShown: showWarning,
      closeWarning: this.closeWarning,
      completeVisitor: this.completeBroker,
      isBuildingLevel,
      isPmcLevel,
      preselectedProperty,
      shouldBePermanent,

      form,
      validate,
      hasSelectedProperty,
      match,
    };
    return <BrokerFormComponent {...props} />;
  }
}

BrokerFormContainer.defaultProps = {
  pmcId: null,
  buildingId: null,
  brokerId: null,
  isBuildingLevel: null,
  isPmcLevel: null,
  propertyId: null,
  propertyName: null,
  formValues: {},
  formInitialValues: {},
  filteredBuildings: null,
  buildings: [],
};

BrokerFormContainer.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  getPmcs: PropTypes.func.isRequired,
  getBuildings: PropTypes.func.isRequired,
  getBuildingDoors: PropTypes.func.isRequired,
  getBrokerById: PropTypes.func.isRequired,
  onSubmitComplete: PropTypes.func.isRequired,
  shouldBePermanent: PropTypes.func.isRequired,
  pmcId: PropTypes.string,
  buildingId: PropTypes.string,
  brokerId: PropTypes.string,
  propertyId: PropTypes.string,
  propertyName: PropTypes.string,
  completeData: PropTypes.instanceOf(Object).isRequired,
  formValues: PropTypes.instanceOf(Object),
  match: PropTypes.instanceOf(Object).isRequired,
  pmcs: PropTypes.instanceOf(Object).isRequired,
  filteredBuildings: buildingsForFilterTypes,
  isEditable: PropTypes.bool.isRequired,
  isUpdate: PropTypes.bool.isRequired,
  isBuildingLevel: PropTypes.bool,
  isPmcLevel: PropTypes.bool,
  hasSelectedProperty: PropTypes.bool.isRequired,
  doors: PropTypes.instanceOf(Array).isRequired,
  updateFormValue: PropTypes.func.isRequired,
  getPmcDoors: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  formInitialValues: PropTypes.instanceOf(Object),
  buildings: PropTypes.instanceOf(Array),
};

const mapStateToProps = (state, ownProps) => {
  const { buildings, pmcs, doors, selectExisting, visitors } = state;
  const { brokerId, buildingId, PMCId: pmcId } = ownProps?.match?.params || {};
  const formValues = getFormValues(form)(state);
  const formInitialValues = getFormInitialValues(form)(state);

  const selectedPmc = pmcId ? pmcs?.[pmcId] : null;
  const pmcName = selectedPmc?.name;

  let filteredBuildings = buildings;
  const selectedPmcId = pmcId || formValues?.pmc;
  if (selectedPmcId?.length > 0) {
    filteredBuildings = filterObjectByKeyValues(
      filteredBuildings,
      'parent.id',
      selectedPmcId,
    );
  }

  const selectedBuildingId = buildingId || formValues?.buildings?.[0];
  let selectedBuilding = null;
  if (selectedBuildingId) {
    selectedBuilding = buildings?.[selectedBuildingId];
  }
  const buildingName = selectedBuilding?.name;

  const isBuildingLevel = !!buildingId;
  const isPmcLevel = !!pmcId;
  const propertyId = buildingId || pmcId;
  const propertyName = buildingId ? buildingName : pmcId ? pmcName : null;

  const broker = visitors?.[brokerId];
  const hasSelectedProperty = Boolean(
    selectedPmcId?.length || selectedBuildingId,
  );
  const doorsFromBuilding = getDoorsFromBuilding(
    filteredBuildings,
    formValues?.buildings,
    !!selectedPmcId?.length,
  );

  const filteredDoors = getBrokerDoors(doors, doorsFromBuilding);
  const formattedDoors = formatDoorName(filteredDoors);

  return {
    brokerId,
    broker,
    pmcId,
    buildingId,
    isBuildingLevel,
    isPmcLevel,
    propertyId,
    propertyName,
    pmcs,
    filteredBuildings,
    doors: formattedDoors,
    hasSelectedProperty,
    isEditable: !broker ? true : get(broker, 'person.editable'),
    completeData: get(selectExisting, 'people', {}),
    formValues,
    isUpdate: !!brokerId,
    formInitialValues,
    form,
  };
};

const type = 'Broker';

const mapDispatchToProps = (dispatch, ownProps) => ({
  getBrokerById: (brokerId) => getVisitorInfoById(dispatch, brokerId),
  onSubmit: (data) => onPersonSubmit(dispatch, data, type, ownProps),
  onSubmitComplete: (data) => onSubmitComplete(dispatch, data, type, ownProps),
  getPmcs: () => getAllPmcReducedData(dispatch),
  getBuildings: () => getBuildingsForFilter(dispatch),
  getPmcDoors: (pmcId, callback = () => null) =>
    getPmcDoors(dispatch, {}, pmcId, false, false, callback),
  getBuildingDoors: (buildingId, callback = () => null) =>
    getDoors(dispatch, {}, buildingId, false, false, callback),
  shouldBePermanent: () =>
    confirmToBePermanent(dispatch, i18n.t('roles.brokers')),
  updateFormValue: (field, value) => dispatch(change(form, field, value)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTransl()(BrokerFormContainer));
